import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import FieldRow from 'components/FormElements/FieldRow';
import Spacing from 'components/Spacing';
import { AppNumberInput, AppSimpleSelect } from 'components/FormElements';
import PageSection from 'components/PageSection';
import { styled, TextField, Tooltip, Typography } from '@mui/material';
import {
  LpFeeStartDate,
  LpArrearsCalculation,
} from 'further-types/lp/lp-fee-template';
import Button from 'components/Button';
import FieldSwitch from 'components/FormElements/FieldSwitch';
import { Row } from 'components/Layout';
import { InferredFeeSettingsFormType } from './FormWrapper';
import FeeTemplateFeesTable from './FeeTemplateFeesTable';
import { FeeApportionmentBasis } from 'further-types/lp/lp-fee-template';
import { FeesEqualisationTreatment } from 'further-types/lp/equalisation';
import { FeesEqualisationTreatmentLabels } from 'further-ui/labels';

const StyledAppNumberInput = styled(AppNumberInput)<{
  hasApprovedFees?: boolean;
}>(({ theme, hasApprovedFees = false }) => ({
  backgroundColor: hasApprovedFees ? theme.palette.grey[100] : 'transparent',
}));

const feeApportionmentBasisOptions = [
  {
    value: FeeApportionmentBasis.OneNumberOfPeriods,
    label: '1/Number of periods',
  },
  { value: FeeApportionmentBasis.ActualActual, label: 'Actual/actual' },
];

const feesEqualisationTreatmentOptions = Object.entries(
  FeesEqualisationTreatmentLabels,
).map(([value, label]) => ({
  value,
  label,
}));

const feeApportionmentBasisTooltipMap = {
  [FeeApportionmentBasis.OneNumberOfPeriods]: (
    <p>
      <b>1/number of periods</b> - This is 1 divided by the accrual frequency,
      e.g. quarterly is 1/4th of the annual fee per quarter.
    </p>
  ),
  [FeeApportionmentBasis.ActualActual]: (
    <p>
      <b>Actual/actual</b> - This calculates the fee on a daily basis and
      multiplies by the number of days in the period (e.g. 90-92 days per
      quarter).
    </p>
  ),
};

const feeStartDateOptions = [
  {
    value: LpFeeStartDate.FundFirstCloseDate,
    label: 'Fund first close date',
  },
  {
    value: LpFeeStartDate.DateOfFirstFundInvestment,
    label: 'Date of first fund investment',
  },
];

const arrearsCalculationOptions = [
  {
    value: LpArrearsCalculation.BasedOnPeriodEndingAmount,
    label: 'Based on period ending amount',
  },
];

const FeeTemplateSection: FC<{
  index: number;
  remove: (index: number) => void;
  hasApprovedFees: boolean;
  hasCommitments: boolean;
  setHasEditedTemplateWithCommitment: (
    hasEditedTemplateWithCommitment: boolean,
  ) => void;
}> = ({
  index,
  remove,
  hasApprovedFees,
  hasCommitments,
  setHasEditedTemplateWithCommitment,
}) => {
  const { control, setValue, getValues, watch } =
    useFormContext<InferredFeeSettingsFormType>();

  const lpFeeTemplates = getValues('lpFeeTemplates');
  const feeTemplateEqualisationTreatment = watch(
    `lpFeeTemplates.${index}.feesEqualisationTreatment`,
  );

  const handleDefaultTemplateChange = (checked: boolean) => {
    if (checked) {
      lpFeeTemplates.forEach((_, i) => {
        if (i !== index) {
          setValue(`lpFeeTemplates.${i}.isDefaultFeeTemplate`, false, {
            shouldValidate: true,
          });
        }
      });
    }
  };

  const templateInUseMessage = hasApprovedFees
    ? 'This field is disabled because it has approved fees.'
    : undefined;

  const disabledUntilLaterDate = true;

  return (
    <PageSection
      showDivider={index !== 0}
      headerContent={
        <Row justify="space-between">
          <Typography variant="h4">Core fee template {index + 1}</Typography>
          {lpFeeTemplates.length > 1 ? (
            <Button
              variant="outlined"
              size="large"
              type="button"
              color="error"
              onClick={() => remove(index)}
              disabled={hasCommitments || hasApprovedFees}
              tooltip={
                hasCommitments || hasApprovedFees
                  ? 'It is not possible to delete a fee template that has commitments or approved fees.'
                  : undefined
              }
            >
              Remove fee template
            </Button>
          ) : null}
        </Row>
      }
    >
      <Spacing>
        <FieldRow title="Name" centerTitle>
          <Controller
            name={`lpFeeTemplates.${index}.name`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                placeholder="e.g. LPA"
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
          />
        </FieldRow>
        <Controller
          name={`lpFeeTemplates.${index}.feeApportionmentBasis`}
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <FieldRow
              title="Fee apportionment basis*"
              centerTitle
              tooltipText={feeApportionmentBasisTooltipMap[value]}
            >
              <Tooltip title={templateInUseMessage}>
                <AppSimpleSelect
                  options={feeApportionmentBasisOptions}
                  fullWidth
                  error={!!error}
                  errorMessage={error?.message}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={hasApprovedFees}
                />
              </Tooltip>
            </FieldRow>
          )}
        />
        <Controller
          name={`lpFeeTemplates.${index}.feesEqualisationTreatment`}
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <FieldRow
              title="Equalisation treatment*"
              centerTitle
              tooltipText={
                'The selected equalisation treatment will be applied to historic fees for new investors joining the vehicle after first close. This fee is editable on a per-investor basis in the Close & Equalise tool.'
              }
            >
              <Tooltip title={templateInUseMessage}>
                <AppSimpleSelect
                  options={feesEqualisationTreatmentOptions}
                  fullWidth
                  error={!!error}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={hasApprovedFees}
                />
              </Tooltip>
            </FieldRow>
          )}
        />
        {feeTemplateEqualisationTreatment ===
          FeesEqualisationTreatment.Partial && (
          <FieldRow title="Equalisation treatment percentage*" centerTitle>
            <Controller
              name={`lpFeeTemplates.${index}.feesEqualisationTreatmentPercentage`}
              control={control}
              render={({ fieldState: { error }, field }) => (
                <StyledAppNumberInput
                  {...field}
                  onChange={(value: number) => {
                    field.onChange(value);
                  }}
                  error={!!error}
                  disabled={hasApprovedFees}
                  helperText={error?.message}
                  decimals={2}
                  percentEndAdornment
                  hasApprovedFees={hasApprovedFees}
                />
              )}
            />
          </FieldRow>
        )}
        <FieldRow
          title="Fee start date"
          centerTitle
          tooltipText="This is the date from which annual fees will begin to accrue."
        >
          <Controller
            name={`lpFeeTemplates.${index}.feeStartDate`}
            control={control}
            render={({ fieldState: { error }, field: { value, onChange } }) => (
              <Tooltip title={templateInUseMessage}>
                <AppSimpleSelect
                  options={feeStartDateOptions}
                  fullWidth
                  error={!!error}
                  errorMessage={error?.message}
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  disabled={hasApprovedFees}
                />
              </Tooltip>
            )}
          />
        </FieldRow>
        <FieldRow title="Arrears calculation" centerTitle>
          <Controller
            name={`lpFeeTemplates.${index}.arrearsCalculation`}
            control={control}
            render={({ fieldState: { error }, field: { value, onChange } }) => (
              <Tooltip title={templateInUseMessage}>
                <AppSimpleSelect
                  options={arrearsCalculationOptions}
                  fullWidth
                  error={!!error}
                  errorMessage={error?.message}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={disabledUntilLaterDate}
                />
              </Tooltip>
            )}
          />
        </FieldRow>
        <FieldRow title="Default fee template" centerTitle>
          <Controller
            name={`lpFeeTemplates.${index}.isDefaultFeeTemplate`}
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <FieldSwitch
                {...rest}
                checked={!!rest.value}
                uncheckedLabel="No"
                checkedLabel="Yes"
                isControlled
                onChange={(e) => {
                  handleDefaultTemplateChange(e.target.checked);
                  onChange(e.target.checked);
                }}
              />
            )}
          />
        </FieldRow>
        <FeeTemplateFeesTable
          index={index}
          hasApprovedFees={hasApprovedFees}
          hasCommitments={hasCommitments}
          templateInUseMessage={templateInUseMessage}
          setHasEditedTemplateWithCommitment={
            setHasEditedTemplateWithCommitment
          }
        />
      </Spacing>
    </PageSection>
  );
};

export default FeeTemplateSection;
